import React, { useState, useEffect, useRef, useContext } from 'react';
import classNames from 'classnames/bind';
import { Typography, Button, Icon, useMediaQuery, breakpoints } from 'gd-design-library';
import { Collapse } from '@mui/material';
import searchStar from '../../assets/images/search-star.svg';
import styles from './SmartSearch.module.scss';
import { useServiceRef } from '../../hooks';
import { SearchService } from '@services';
import {
  AppliedFilter,
  ConversationalMessage,
  ConversationalProductsSearchResponse,
  SuggestedAnswer,
  PreviousSuccessfulSearchRequest
} from '../../services/search/interfaces';
import { useRouter } from 'next/router';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';
import { ChatInput } from './components/ChatInput';
import { Messages } from './components/Messages';
import { base64ToFile } from './utils';
import { ChatMessage, ChatMessageType, SmartSearchContext } from '../../utils/SmartSearchContext';
import { QueryType } from '@constants';

const cx = classNames.bind(styles);
const CN = 'smart-search';
const headerClassName = `${CN}__header`;
const titleClassName = `${CN}__title`;
const toggleButtonClassName = `${CN}__toggle-button`;
const contentClassName = `${CN}__content`;

const SEARCH_RESULTS_MESSAGE = 'Here are some results based on your query and selected filters.';

interface SmartSearchProps {
  title?: string;
  isExpanded?: boolean;
  children?: React.ReactNode;
  className?: string;
  initialMessage?: string;
  filters?: AppliedFilter[];
  sort?: string;
}

export const SmartSearch: React.FC<SmartSearchProps> = ({
  title = 'Smart Search',
  children,
  className,
  initialMessage = '',
  filters,
  sort
}) => {
  const [ isLoading, setIsLoading ] = useState(false);
  const {
    chatHistory,
    setChatHistory,
    lastResponse,
    setLastResponse,
    originalQuery,
    setOriginalQuery,
    isSmartSearchOpen,
    setIsSmartSearchOpen
  } = useContext(SmartSearchContext);
  const [ currentSuggestedAnswers, setCurrentSuggestedAnswers ] = useState<SuggestedAnswer[]>([]);
  const [ previousSuccessfulSearchRequest, setPreviousSuccessfulSearchRequest ] =
    useState<PreviousSuccessfulSearchRequest | null>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [ conversationId, setConversationId ] = useState<string | null>(null);
  const [ refinedQueries, setRefinedQueries ] = useState<string[] | null>(null);
  const [ conversationContents, setConversationContents ] = useState<ConversationalMessage[]>([]);
  const [ lastFailedRequest, setLastFailedRequest ] = useState<{
    type: 'message' | 'answer';
    params: {
      userMessage?: string;
      answer?: SuggestedAnswer;
    };
  } | null>(null);
  const [ currentFilters, setCurrentFilters ] = useState<AppliedFilter[]>([]);
  const [ currentSort, setCurrentSort ] = useState<string | undefined>(undefined);
  const lastInitialMessageRef = useRef<string | null>(null);
  const searchServiceRef = useServiceRef(SearchService);
  const router = useRouter();
  const isMobileOrTablet = useMediaQuery(`(max-width: ${breakpoints.md})`);
  const isConversationalSearchPage = router.query['conversational-search'] === 'true';

  const addChatMessage = (content: string, type: ChatMessageType, image?: File): void => {
    if (content === SEARCH_RESULTS_MESSAGE &&
      chatHistory[chatHistory.length - 1]?.content.includes(SEARCH_RESULTS_MESSAGE)) return;

    const message: ChatMessage = {
      id: `${Date.now()}-${Math.random()}`,
      type,
      content,
      image,
      timestamp: Date.now()
    };

    setChatHistory(prev => [ ...prev, message ]);
  };

  const resetSuggestedAnswers = (): void => {
    setCurrentSuggestedAnswers([]);
  };

  const clearFailedRequest = (): void => {
    setLastFailedRequest(null);
  };

  const retryLastRequest = async (): Promise<void> => {
    if (!lastFailedRequest) return;

    clearFailedRequest();

    if (lastFailedRequest.type === 'message' && lastFailedRequest.params.userMessage) {
      await performSearch(lastFailedRequest.params.userMessage);
    } else if (lastFailedRequest.type === 'answer' && lastFailedRequest.params.answer) {
      await performAnswerSearch(lastFailedRequest.params.answer);
    }
  };

  const performSearch = async (userMessage?: string, isNewConversation = false): Promise<void> => {
    setIsLoading(true);

    try {
      const searchService = searchServiceRef.current;

      let response: ConversationalProductsSearchResponse | null = null;

      if (isNewConversation) {
        response = await searchService.getConversationalProductsSearch({
          query: userMessage,
          filters: filters || [],
          ...(sort && { sort })
        });
      } else {
        response = await searchService.getConversationalProductsSearch({
          query: !conversationId ? userMessage : (originalQuery || userMessage),
          ...(conversationId && { conversationId }),
          ...(refinedQueries && { refinedQueries }),
          ...(conversationContents.length > 0 && { contents: conversationContents }),
          ...(conversationId && { textAnswer: userMessage }),
          filters: filters || [],
          ...(previousSuccessfulSearchRequest && { previousSuccessfulSearchRequest }),
          ...(sort && { sort })
        });
      }

      if (response) {
        const followUp = response.conversationalFilteringResult?.followUpQuestion;

        if (response.conversationalTextResponse) {
          addChatMessage(`${response.conversationalTextResponse} ${followUp?.question ? `\n\n${followUp.question}` : ''}`, ChatMessageType.AI);
        }

        if (!response.conversationalTextResponse && !followUp?.question) {
          const resultsCount = Object.values(response.products || {}).reduce((acc, section) => acc + (section?.total || 0), 0);
          const message = resultsCount > 0 ? `Results for ${response.refinedQueries?.join(', ')} (${resultsCount})` : 'No results found';
          addChatMessage(message, ChatMessageType.AI);
        }

        if (followUp?.suggestedAnswers) {
          setCurrentSuggestedAnswers(followUp.suggestedAnswers);
        }

        updateConversationState(response);
        clearFailedRequest();

        const isOrderSupport = response.queryTypes?.includes(QueryType.ORDER_SUPPORT);
        if (!isOrderSupport) setLastResponse(response);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in conversational search:', error);
      setLastFailedRequest({
        type: 'message',
        params: { userMessage }
      });
      if (!isSmartSearchOpen) {
        setIsSmartSearchOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const performAnswerSearch = async (answer: SuggestedAnswer): Promise<void> => {
    setIsLoading(true);

    try {
      const searchService = searchServiceRef.current;

      const response = await searchService.getConversationalProductsSearch({
        query: originalQuery || '',
        ...(conversationId && { conversationId }),
        ...(refinedQueries && { refinedQueries }),
        ...(conversationContents.length > 0 && { contents: conversationContents }),
        answer,
        ...(previousSuccessfulSearchRequest && { previousSuccessfulSearchRequest }),
        ...(sort && { sort })
      });

      if (response) {
        const followUp = response.conversationalFilteringResult?.followUpQuestion;

        if (response.conversationalTextResponse) {
          addChatMessage(response.conversationalTextResponse, ChatMessageType.AI);
        }

        if (followUp?.question) {
          addChatMessage(followUp.question, ChatMessageType.AI);
        }

        if (!response.conversationalTextResponse && !followUp?.question) {
          const resultsCount = Object.values(response.products || {}).reduce((acc, section) => acc + (section?.total || 0), 0);
          const message = resultsCount > 0 ? `Results for ${response.refinedQueries?.join(', ')} (${resultsCount})` : 'No results found';
          addChatMessage(message, ChatMessageType.AI);
        }

        if (followUp?.suggestedAnswers) {
          setCurrentSuggestedAnswers(followUp.suggestedAnswers);
        }

        updateConversationState(response);
        clearFailedRequest();

        const isOrderSupport = response.queryTypes?.includes(QueryType.ORDER_SUPPORT);
        if (!isOrderSupport) setLastResponse(response);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in conversational search with answer:', error);
      setLastFailedRequest({
        type: 'answer',
        params: { answer }
      });
      if (!isSmartSearchOpen) {
        setIsSmartSearchOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateConversationState = (response: ConversationalProductsSearchResponse): void => {
    if (response.previousSuccessfulSearchRequest) {
      setPreviousSuccessfulSearchRequest(response.previousSuccessfulSearchRequest);
    }

    if (response.conversationId) {
      setConversationId(response.conversationId);
    }

    if (response.refinedQueries) {
      setRefinedQueries(response.refinedQueries);
    }

    if (response.contents) {
      setConversationContents(response.contents);
    }
  };

  const handleSendMessage = async (searchQuery: string): Promise<void> => {
    setIsLoading(true);

    if (!conversationId) {
      setOriginalQuery(searchQuery);
    }

    await performSearch(searchQuery);
    setIsLoading(false);
  };

  const handleSuggestedAnswerClick = async (answer: SuggestedAnswer): Promise<void> => {
    if (isLoading) return;

    resetSuggestedAnswers();
    addChatMessage(answer.displayValue || answer.value, ChatMessageType.USER);

    await performAnswerSearch(answer);
  };

  // Auto-send initial message and perform search when search page is in conversational mode
  // Handles initial message processing, image uploads from session storage, and chat state reset
  useEffect(() => {
    const trimmedMessage = initialMessage?.trim();

    const shouldSendInitialMessage =
      isConversationalSearchPage &&
      trimmedMessage &&
      !isLoading &&
      trimmedMessage !== originalQuery;

    if (!shouldSendInitialMessage) return;

    lastInitialMessageRef.current = trimmedMessage;

    const resetChatState = (): void => {
      setChatHistory([]);
      setLastResponse(null);
      setConversationId(null);
      setRefinedQueries(null);
      setConversationContents([]);
      setCurrentSuggestedAnswers([]);
      setPreviousSuccessfulSearchRequest(null);
    };

    const sessionUserMessage = sessionStorage.getItem('smartSearchUserMessage');
    const sessionImageBase64 = sessionStorage.getItem('smartSearchUploadedImage');
    const sessionImageToQueryResult = sessionStorage.getItem('smartSearchImageToQueryResult');

    const isRedirectedSmartSearch = sessionUserMessage || sessionImageBase64 || sessionImageToQueryResult;

    if (isRedirectedSmartSearch) {
      sessionStorage.removeItem('smartSearchUserMessage');
      let imageFile: File | undefined;

      if (sessionImageBase64 || sessionImageToQueryResult) {
        try {
          imageFile = base64ToFile(sessionImageBase64, 'uploadedImage.png');
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error converting base64 to file:', error);
        } finally {
          sessionStorage.removeItem('smartSearchUploadedImage');
        }
      }

      const searchQuery = sessionImageToQueryResult || sessionUserMessage;

      sessionStorage.removeItem('smartSearchImageToQueryResult');
      resetChatState();
      addChatMessage(sessionUserMessage, ChatMessageType.USER, imageFile);
      setOriginalQuery(searchQuery);
      performSearch(searchQuery, true);

      return;
    }

    const searchQuery = trimmedMessage;

    resetChatState();
    setOriginalQuery(searchQuery);
    addChatMessage(trimmedMessage, ChatMessageType.USER);
    performSearch(searchQuery, true);
  }, [ isConversationalSearchPage, initialMessage, originalQuery ]);

  // Auto-perform search when filters change
  useEffect(() => {
    if (
      isConversationalSearchPage &&
      chatHistory.length > 0 &&
      filters &&
      currentFilters &&
      JSON.stringify(currentFilters) !== JSON.stringify(filters)
    ) {
      resetSuggestedAnswers();
      performSearch();
      setCurrentFilters(filters);
    }
  }, [ filters, currentFilters ]);

  // Auto-perform search when sort changes
  useEffect(() => {
    if (
      isConversationalSearchPage &&
      chatHistory.length > 0 &&
      sort !== currentSort &&
      currentSort !== undefined
    ) {
      resetSuggestedAnswers();
      performSearch();
    }
    setCurrentSort(sort);
  }, [ sort ]);

  // Reset the last initial message when component collapses
  useEffect(() => {
    if (!isSmartSearchOpen) {
      lastInitialMessageRef.current = null;
    }
  }, [ isSmartSearchOpen ]);

  // Close smart search when navigating away from search page
  useEffect(() => {
    if (!isConversationalSearchPage && isSmartSearchOpen) {
      setIsSmartSearchOpen(false);
    }
  }, [ isConversationalSearchPage ]);

  // Open smart search when last response is not a simple search
  useEffect(() => {
    const isSimpleSearch = lastResponse?.queryTypes?.includes(QueryType.SIMPLE_PRODUCT_SEARCH);
    if (isConversationalSearchPage && lastResponse && !isSimpleSearch) {
      setIsSmartSearchOpen(true);
    }
  }, [ isConversationalSearchPage, lastResponse?.queryTypes ]);

  const scrollToLastMessageTop = (): void => {
    if (chatScrollRef.current && chatHistory.length > 0) {
      requestAnimationFrame(() => {
        if (!chatScrollRef.current) return;
        const lastMessageId = chatHistory[chatHistory.length - 1].id;
        const lastMessageElement = chatScrollRef.current.querySelector(`[data-message-id="${lastMessageId}"]`) as HTMLElement;

        if (lastMessageElement) {
          const container = chatScrollRef.current;
          const messageTop = lastMessageElement.offsetTop;
          container.scrollTop = messageTop;
        }
      });
    }
  };

  const scrollToBottom = (): void => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (!isConversationalSearchPage) return;
    scrollToLastMessageTop();
  }, [ chatHistory, isConversationalSearchPage ]);

  if (!isFeatureEnabled(FEATURES.SMART_SEARCH)) {
    return null;
  }

  return isMobileOrTablet ? (
    <>
      {isConversationalSearchPage && (
        <Messages
          chatHistory={ chatHistory }
          isLoading={ isLoading }
          lastFailedRequest={ lastFailedRequest }
          currentSuggestedAnswers={ currentSuggestedAnswers }
          chatScrollRef={ chatScrollRef }
          onSuggestedAnswerClick={ handleSuggestedAnswerClick }
          onRetryRequest={ retryLastRequest }
          scrollToBottom={ scrollToBottom }
        />
      )}
      <ChatInput
        isLoading={ isLoading }
        onSendMessage={ handleSendMessage }
        addChatMessage={ addChatMessage }
        resetSuggestedAnswers={ resetSuggestedAnswers }
        setIsLoading={ setIsLoading }
      />
    </>
  ) : (
    <div className={ cx(CN, className, { [`${CN}--expanded`]: isSmartSearchOpen }) }>
      <div className={ cx(headerClassName) } onClick={ (): void => setIsSmartSearchOpen(!isSmartSearchOpen) }>
        <Typography className={ cx(titleClassName) }>
          <img src={ searchStar.src } alt='SearchStar' />
          {title}
        </Typography>
        <Button
          variant='inherit'
          isIcon
          className={ cx(toggleButtonClassName, { [`${toggleButtonClassName}--expanded`]: isSmartSearchOpen }) }
          onClick={ (): void => setIsSmartSearchOpen(!isSmartSearchOpen) }
        >
          <Icon name='arrowDown' width={ 24 } height={ 24 } />
        </Button>
      </div>
      <Collapse in={ isSmartSearchOpen } timeout='auto'>
        <div
          className={ cx(contentClassName) }
          onTransitionEnd={ (e): void => { if (e.propertyName === 'height') { scrollToBottom(); } } }
        >
          <Messages
            chatHistory={ isConversationalSearchPage ? chatHistory : [] }
            isLoading={ isLoading }
            lastFailedRequest={ lastFailedRequest }
            currentSuggestedAnswers={ currentSuggestedAnswers }
            chatScrollRef={ chatScrollRef }
            onSuggestedAnswerClick={ handleSuggestedAnswerClick }
            onRetryRequest={ retryLastRequest }
            scrollToBottom={ scrollToBottom }
          />
          <ChatInput
            isLoading={ isLoading }
            onSendMessage={ handleSendMessage }
            addChatMessage={ addChatMessage }
            resetSuggestedAnswers={ resetSuggestedAnswers }
            setIsLoading={ setIsLoading }
          />
          {children}
        </div>
      </Collapse>
    </div>
  );
};
